import * as React from 'react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import CustomDataGridToolbar from './CustomDataGridToolbar';
import TableToolbar from './MUITable/TableToolbar';
import { MobergFontFamily, MobergFontSize } from './MobergFont/MobergFont';
import { useFetchTableData } from '../Hooks/useFetchTableData';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTableUrlSync } from './ModernMUITable/useTableUrlSync';

/**
 * IMPORTANT: MUI DataGrid v8 Migration Compatibility
 * 
 * This component has been updated to handle MUI DataGrid v8's breaking change in row selection model format:
 * - v7 and earlier: rowSelectionModel was an array of row IDs (GridRowId[])
 * - v8: rowSelectionModel must be an object: { type: 'include' | 'exclude', ids: Set<GridRowId> }
 * 
 * The internal selectors (like gridRowSelectionSelector) in v8 expect this structure and will throw
 * "Cannot read properties of undefined (reading 'size')" if rowSelectionModel is undefined or invalid.
 * 
 * This component now always provides a valid v8 selection model structure, even when selection is disabled.
 */

function ensureRowIds(rows) {
  return (rows || []).map((row, idx) => ({
    ...row,
    id: row.id ?? row.patient_id ?? row._id ?? idx
  }));
}


const convertArrayToSelectionModel = (arraySelection) => {
  if (!Array.isArray(arraySelection)) return { type: 'include', ids: new Set() };
  return {
    type: 'include',
    ids: new Set(arraySelection)
  };
};


const ensureValidSelectionModel = (selectionModel) => {
  if (!selectionModel || typeof selectionModel !== 'object') {
    return { type: 'include', ids: new Set() };
  }
  
  // Handle array format (old MUI X format) - convert to new format
  if (Array.isArray(selectionModel)) {
    return { type: 'include', ids: new Set(selectionModel) };
  }
  
  if (!selectionModel.type || !['include', 'exclude'].includes(selectionModel.type)) {
    return { type: 'include', ids: new Set() };
  }
  
  if (!selectionModel.ids || !(selectionModel.ids instanceof Set)) {
    return { type: selectionModel.type, ids: new Set() };
  }
  
  return selectionModel;
};

const convertSelectionModelToArray = (selectionModel, allRowIds = []) => {
  if (!selectionModel || !selectionModel.ids || !(selectionModel.ids instanceof Set)) {
    return [];
  }
  
  if (selectionModel.type === 'exclude') {
    // For exclude type: return all row IDs except those in the exclude set
    const excludeIds = Array.from(selectionModel.ids);
    return allRowIds.filter(id => !excludeIds.includes(id));
  } else {
    // For include type: return the IDs in the include set
    return Array.from(selectionModel.ids);
  }
};

export default function ModernMUITable({
  // Data fetching props (optional)
  queryKey,
  endpoint,
  body = {},
  optionalParams,
  supportsPageSize = false, // New prop to control whether to send page size to API
  
  columns,
  tableData,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  paginationMode = 'client',
  loading = false,
  isRowSelectable,
  checkboxSelection = false,
  disableSelectAll = false,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  sortingMode = 'client',
  onSortModelChange,
  toolbar,
  tableToolbarProps = {},
  showToolbar = true,
  height = 'auto',
  dense = false,
  disableRowSelectionOnClick = true,
  onRowSelectionModelChange = () => {},
  rowSelectionModel,
  tableRef,
  overrideStyles = {},
  disableHighlightRows = false,
  // New row selection enhancement props
  enableRowSelection = false,
  onSelectionChange = () => {},
  // Advanced toolbar functionality
  useAdvancedToolbar = false,
  // Pagination options
  pageSizeOptions = [10, 25, 50, 100],
  initialState,
  
  // URL STATE PERSISTENCE PROPS
  persistStateInUrl = false, // Enable URL parameter persistence
  tableId = 'table', // Unique identifier for this table (for multiple tables on same page)
  urlSyncDebounceMs = 300, // Debounce delay for URL updates
  
  // DEFAULT SORTING PROP
  // Use this prop to set initial sorting when the table loads
  // Format: [{ field: 'columnName', sort: 'asc' | 'desc' }]
  // Examples:
  // - Sort by patient_id ascending: [{ field: 'patient_id', sort: 'asc' }]
  // - Sort by last_name descending: [{ field: 'last_name', sort: 'desc' }]
  // - Multi-column sort: [{ field: 'site_name', sort: 'asc' }, { field: 'patient_id', sort: 'asc' }]
  // Note: This only applies when no URL state exists (first load or no persistence)
  defaultSortModel = null,
  
  // BACKWARD COMPATIBILITY PROPS FOR OLD MUTABLE API
  handleOnRowClick, // Function to handle row clicks (old API)
  isCheckboxSelection, // Boolean for checkbox selection (old API) - maps to checkboxSelection
  disableMultiSelect, // Function or boolean for multi-select behavior (old API)
  preselectedProps, // Object with selectedRows and setSelectedRows (old API)
  isAutoselect = false, // Boolean for auto-selecting single rows (old API)
  paginationType, // Pagination type (old API)
  onSearchValueChanged = () => {}, // Search change callback (old API)
  autoPageSizeProps = { enabled: false }, // Auto page size configuration (old API)
  
  ...rest
}) {
  // URL state persistence setup
  const urlSync = useTableUrlSync({
    enabled: persistStateInUrl,
    tableId,
    debounceMs: urlSyncDebounceMs,
    initialState: {
      sortModel: [],
      search: '',
      pagination: initialState?.pagination?.paginationModel || { page: 0, pageSize: 10 }
    }
  });

  // Internal state for data fetching - use URL state if available
  const [internalPaginationModel, setInternalPaginationModel] = useState(() => {
    if (persistStateInUrl && urlSync.hasUrlState && urlSync.urlState.pagination) {
      return urlSync.urlState.pagination;
    }
    if (initialState?.pagination?.paginationModel) {
      return initialState.pagination.paginationModel;
    }
    return { page: 0, pageSize: 10 };
  });
  
  const [internalSortModel, setInternalSortModel] = useState(() => {
    if (persistStateInUrl && urlSync.hasUrlState && urlSync.urlState.sortModel) {
      return urlSync.urlState.sortModel;
    }
    // Use defaultSortModel if provided and no URL state exists
    if (defaultSortModel && Array.isArray(defaultSortModel) && defaultSortModel.length > 0) {
      return defaultSortModel;
    }
    return [];
  });
  
  const [internalQueryOptions, setInternalQueryOptions] = useState(() => {
    const search = persistStateInUrl && urlSync.hasUrlState && urlSync.urlState.search 
      ? urlSync.urlState.search 
      : '';
    return { search_value: search };
  });

  // Internal row selection state for advanced toolbar
  const [internalRowSelectionModel, setInternalRowSelectionModel] = React.useState(() => {
    if (preselectedProps && preselectedProps.selectedRows) {
      return preselectedProps.selectedRows;
    }
    return [];
  });

  // Direct controlled row selection state for MUI X v8 (like official docs)
  // Always initialize with a valid v8 selection model structure to prevent undefined errors
  const [controlledRowSelectionModel, setControlledRowSelectionModel] = React.useState(() => {
    const defaultModel = { type: 'include', ids: new Set() };
    return ensureValidSelectionModel(defaultModel);
  });

  // BACKWARD COMPATIBILITY LOGIC
  // Handle isCheckboxSelection mapping to checkboxSelection
  const finalCheckboxSelection = isCheckboxSelection !== undefined ? isCheckboxSelection : checkboxSelection;
  
  // Determine if row selection should be enabled
  const isRowSelectionEnabled = finalCheckboxSelection || enableRowSelection || useAdvancedToolbar;
  
  // Handle preselected rows from old API - convert to MUI X v8 format
  const [backwardCompatRowSelection, setBackwardCompatRowSelection] = useState(
    preselectedProps ? (preselectedProps.selectedRows || []) : []
  );
  
  // Use preselected rows if provided, otherwise use the new API rowSelectionModel
  const baseRowSelectionArray = useMemo(() => {
    if (preselectedProps) {
      return backwardCompatRowSelection || [];
    }
    return rowSelectionModel ? convertSelectionModelToArray(rowSelectionModel) : [];
  }, [preselectedProps, backwardCompatRowSelection, rowSelectionModel]);
  
  const baseRowSelectionModel = useMemo(() => {
    return convertArrayToSelectionModel(baseRowSelectionArray);
  }, [baseRowSelectionArray]);

  // Ensure we always have a valid selection model for this render
  const safeRowSelectionModel = useMemo(() => {
    return ensureValidSelectionModel(controlledRowSelectionModel || { type: 'include', ids: new Set() });
  }, [controlledRowSelectionModel]);

  // Handle row click functionality for backward compatibility
  const handleRowClick = (params) => {
    if (handleOnRowClick) {
      handleOnRowClick(params.row);
    }
  };

  // Determine if we should use data fetching
  const shouldFetchData = !!(queryKey && endpoint);
  
  // Use provided pagination model or internal one
  const currentPaginationModel = paginationModel || internalPaginationModel;
  
  // Ensure DataGrid gets the correct format (page, pageSize)
  const dataGridPaginationModel = {
    page: currentPaginationModel.page || currentPaginationModel.pageIndex || 0,
    pageSize: currentPaginationModel.pageSize || 10
  };

  // Determine modes early for fetchParams
  const finalSortingMode = tableData ? "client" : sortingMode;
  const finalPaginationMode = tableData ? "client" : paginationMode;

  // Data fetching (conditional) - only runs when dependencies change
  const fetchParams = {
    queryKey: shouldFetchData ? queryKey : null,
    endpoint: shouldFetchData ? endpoint : null,
    body: shouldFetchData ? body : {},
    optionalParams: shouldFetchData ? optionalParams : {},
    queryOptions: internalQueryOptions,
    paginationModel: {
      pageIndex: currentPaginationModel.page || currentPaginationModel.pageIndex || 0,
      pageSize: currentPaginationModel.pageSize || 10
    },
    sortModel: internalSortModel,
    supportsPageSize: shouldFetchData ? supportsPageSize : false,
  };
  
  const {
    data: fetchedData,
    isFetching,
    isError,
    refetch
  } = useFetchTableData(fetchParams);

  // Calculate loading state early
  const isLoading = loading || (shouldFetchData && isFetching);

  // Transform columns for backward compatibility (handle valueGetter format changes)
  const transformedColumns = useMemo(() => {
    return columns.map(column => {
      let transformedColumn = { ...column };
      
      // Handle valueGetter format changes
      if (column.valueGetter && typeof column.valueGetter === 'function') {
        // Check if this looks like the old API format by testing the function signature
        const valueGetterStr = column.valueGetter.toString();
        if (valueGetterStr.includes('params.row') || valueGetterStr.includes('params.value')) {
          // Transform old API (params) to new API (value, row)
          const oldValueGetter = column.valueGetter;
          transformedColumn.valueGetter = (value, row) => {
            // Create params object in old format
            const params = { row, value };
            return oldValueGetter(params);
          };
        }
      }
      
      // Handle renderCell format changes and add loading state
      if (column.renderCell && typeof column.renderCell === 'function') {
        const renderCellStr = column.renderCell.toString();
        if (renderCellStr.includes('params.row') || renderCellStr.includes('params.value')) {
          // Transform old API (params) to new API (params with different structure)
          const oldRenderCell = column.renderCell;
          transformedColumn.renderCell = (params) => {
            // The params structure might be slightly different, ensure backward compatibility
            const compatParams = {
              ...params,
              row: params.row,
              value: params.value,
              isLoading: isLoading // Pass loading state to renderCell functions
            };
            return oldRenderCell(compatParams);
          };
        } else {
          // For new API format, wrap the renderCell to include loading state
          const originalRenderCell = column.renderCell;
          transformedColumn.renderCell = (params) => {
            const paramsWithLoading = {
              ...params,
              isLoading: isLoading
            };
            return originalRenderCell(paramsWithLoading);
          };
        }
      }
      
      return transformedColumn;
    });
  }, [columns, isLoading]);

  // Determine data source
  const rows = useMemo(() => {
    if (tableData) {
      return ensureRowIds(tableData);
    }
    if (shouldFetchData && fetchedData?.data) {
      return ensureRowIds(fetchedData.data);
    }
    return [];
  }, [tableData, fetchedData?.data, shouldFetchData]);

  const totalRows = tableData ? tableData.length : (fetchedData?.total || rowCount || 0);
  
  // Only use rowCount for server-side pagination
  const effectiveRowCount = finalPaginationMode === "server" ? totalRows : undefined;
  
  // Pagination handler
  const handlePaginationModelChange = (newPaginationModel) => {
    if (onPaginationModelChange) {
      onPaginationModelChange(newPaginationModel);
    } else {
      setInternalPaginationModel(newPaginationModel);
    }
    
    // Update URL if persistence is enabled
    if (persistStateInUrl) {
      urlSync.updateUrl({
        sortModel: internalSortModel,
        search: internalQueryOptions.search_value || '',
        pagination: newPaginationModel
      });
    }
  };
  
  // Sort handler
  const handleSortModelChange = (newSortModel) => {
    setInternalSortModel(newSortModel);
    if (onSortModelChange) {
      onSortModelChange(newSortModel);
    }
    
    // Update URL if persistence is enabled
    if (persistStateInUrl) {
      urlSync.updateUrl({
        sortModel: newSortModel,
        search: internalQueryOptions.search_value || '',
        pagination: currentPaginationModel
      });
    }
  };

  // Search handler - ensure search triggers API calls
  const handleSearchChange = (searchValue) => {
    const newQueryOptions = {
      ...internalQueryOptions,
      search_value: searchValue || ''
    };
    setInternalQueryOptions(newQueryOptions);
    
    // Reset to first page when searching
    const resetPaginationModel = { page: 0, pageSize: currentPaginationModel.pageSize };
    if (onPaginationModelChange) {
      onPaginationModelChange(resetPaginationModel);
    } else {
      setInternalPaginationModel(resetPaginationModel);
    }
    
    // Update URL if persistence is enabled
    if (persistStateInUrl) {
      urlSync.updateUrl({
        sortModel: internalSortModel,
        search: searchValue || '',
        pagination: resetPaginationModel
      });
    }
  };

  // Column visibility handler - no URL persistence for columns
  const handleColumnVisibilityModelChange = (newColumnVisibilityModel) => {
    if (onColumnVisibilityModelChange) {
      onColumnVisibilityModelChange(newColumnVisibilityModel);
    }
    // Column visibility is not persisted in URL as requested
  };

  // Auto-select logic for backward compatibility
  useEffect(() => {
    if (isAutoselect && 
        rows && 
        rows.length === 1 && 
        (!baseRowSelectionModel || baseRowSelectionModel.ids.size === 0) &&
        isRowSelectionEnabled) {
      const autoSelectedRows = [rows[0].id];
      
      if (preselectedProps) {
        setBackwardCompatRowSelection(autoSelectedRows);
        if (preselectedProps.setSelectedRows) {
          preselectedProps.setSelectedRows(autoSelectedRows);
        }
      }
      
      if (typeof disableMultiSelect === 'function') {
        disableMultiSelect(rows[0], true);
      }
    }
  }, [isAutoselect, rows.length, baseRowSelectionModel.ids.size, isRowSelectionEnabled]);

  // Handle preselectedProps changes for backward compatibility
  useEffect(() => {
    if (preselectedProps && preselectedProps.selectedRows !== undefined) {
      setBackwardCompatRowSelection(preselectedProps.selectedRows || []);
    }
  }, [preselectedProps?.selectedRows]);

  // Initialize controlled row selection model with existing selection
  // Always ensure we have a valid selection model structure for MUI X v8
  useEffect(() => {
    try {
      const initialSelection = convertArrayToSelectionModel(baseRowSelectionArray);
      setControlledRowSelectionModel(ensureValidSelectionModel(initialSelection));
    } catch (error) {
      console.warn('Error setting row selection model:', error);
      setControlledRowSelectionModel({ type: 'include', ids: new Set() });
    }
  }, [baseRowSelectionArray.join(',')]);  // Use join to create stable dependency

  // Row selection handler for MUI X v8 format
  const handleRowSelectionModelChange = useCallback((newRowSelectionModel) => {
    // Ensure we always have a valid v8 selection model structure
    const validatedModel = ensureValidSelectionModel(newRowSelectionModel);
    
    // Update the controlled state for visual feedback
    setControlledRowSelectionModel(validatedModel);
    
    // Convert MUI X v8 format to array for internal state management
    const availableRowIds = rows.map(row => row.id);
    const selectionArray = convertSelectionModelToArray(validatedModel, availableRowIds);
    
    // Update internal state for advanced toolbar
    if (useAdvancedToolbar) {
      setInternalRowSelectionModel(selectionArray);
    }

    // BACKWARD COMPATIBILITY: Handle old API patterns
    if (preselectedProps) {
      setBackwardCompatRowSelection(selectionArray);
      if (preselectedProps.setSelectedRows) {
        preselectedProps.setSelectedRows(selectionArray);
      }
    }
    
    // Call original callbacks with proper formats
    if (onRowSelectionModelChange) {
      onRowSelectionModelChange(validatedModel); // MUI X v8 format
    }
    if (onSelectionChange) {
      onSelectionChange(selectionArray); // Array format for easier use
    }
  }, [useAdvancedToolbar, preselectedProps, onRowSelectionModelChange, onSelectionChange]);

  // Handle search change for backward compatibility
  const handleSearchChangeCompat = (searchValue) => {
    handleSearchChange(searchValue);
    if (onSearchValueChanged && typeof onSearchValueChanged === 'function') {
      onSearchValueChanged(searchValue);
    }
  };

  // Add popover state for toolbar compatibility
  const [columnsPanelAnchor, setColumnsPanelAnchor] = useState(null);
  const [isColumnsPanelOpen, setIsColumnsPanelOpen] = useState(false);

  const openFilterPanel = () => {};
  const openColumnsPanel = (event) => {
    setColumnsPanelAnchor(event.currentTarget);
    setIsColumnsPanelOpen(true);
  };
  const closeColumnsPopover = () => {
    setIsColumnsPanelOpen(false);
    setColumnsPanelAnchor(null);
  };

  const setColumnsPopoverRef = (ref) => setColumnsPanelAnchor(ref);

  // Error handling for data fetching
  if (shouldFetchData && isError) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Error loading data. Please try again.</p>
        <button onClick={refetch}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      height: height,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <DataGrid
        autoHeight={height === 'auto'}
        ref={tableRef}
        columns={transformedColumns}
        rows={rows}
        rowCount={effectiveRowCount}
        paginationModel={dataGridPaginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        paginationMode={finalPaginationMode}
        pageSizeOptions={pageSizeOptions}
        loading={isLoading}
        isRowSelectable={isRowSelectable}
        checkboxSelection={isRowSelectionEnabled}
        disableRowSelectionOnClick={isRowSelectionEnabled ? false : disableRowSelectionOnClick}
        disableMultipleRowSelection={disableSelectAll}
        onRowSelectionModelChange={isRowSelectionEnabled ? handleRowSelectionModelChange : undefined}
        rowSelectionModel={safeRowSelectionModel}
        hideFooterSelectedRowCount={!isRowSelectionEnabled}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        sortingMode={finalSortingMode}
        onSortModelChange={handleSortModelChange}
        sortModel={internalSortModel}
        onRowClick={handleRowClick}
        disableColumnFilter
        slots={{ 
          toolbar: useAdvancedToolbar ? TableToolbar : CustomDataGridToolbar
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
          toolbar: useAdvancedToolbar 
            ? { 
                ...tableToolbarProps, 
                rowSelectionModel: internalRowSelectionModel, 
                setRowSelectionModel: setInternalRowSelectionModel,
                setQueryOptions: setInternalQueryOptions,
                onSearchValueChanged: handleSearchChangeCompat,
                openFilterPanel,
                setColumnsPopoverRef,
                columnsPanelAnchor,
                openColumnsPanel,
                isColumnsPanelOpen,
                closeColumnsPopover,
                columnVisibilityModel,
                onColumnVisibilityModelChange: handleColumnVisibilityModelChange
              }
            : { 
                ...tableToolbarProps, 
                setQueryOptions: setInternalQueryOptions,
                // Only pass onSearchChange in server mode (when tableData is not provided)
                // In client mode, let the toolbar use MUI's built-in quick filter
                ...(tableData ? {} : { 
                  onSearchChange: handleSearchChangeCompat,
                  currentSearchValue: internalQueryOptions.search_value || ''
                })
              },
        }}
        initialState={initialState}
        useDefaultMuiStyling={true}
        density={dense ? 'compact' : 'standard'}
        showToolbar={showToolbar}
        sx={{
          ...overrideStyles,
          // Ensure proper height and scrolling
          height: height !== 'auto' ? '100%' : 'auto',
          flex: height !== 'auto' ? 1 : 'none',
          overflow: 'hidden', // Hide outer scrollbar
          backgroundColor: 'var(--color-table-cell-bg) !important',
          color: 'var(--color-table-cell-text) !important',
          // Scrollbar styles are handled globally in index.css on .MuiDataGrid-scrollbar
          // Fix pagination alignment issues
          ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
            "margin-top": "1em",
            "margin-bottom": "1em"
          },
          // Ensure pagination dropdown appears above modals
          '& .MuiDataGrid-menu': {
            zIndex: 10000
          },
          // Apply Moberg fonts
          '& .MuiDataGrid-columnHeaders': {
            fontFamily: MobergFontFamily.HEADER,
            fontWeight: 800,
            fontSize: MobergFontSize.LARGE,
            fontStyle: "initial",
            backgroundColor: 'var(--color-table-header-bg) !important',
            color: 'var(--color-table-cell-text) !important',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: 'var(--color-table-header-bg) !important',
          },
          '& .MuiDataGrid-columnHeader:not(:last-child)': {
            borderRightColor: 'var(--color-table-border)',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontFamily: MobergFontFamily.HEADER,
            color: 'var(--color-table-cell-text) !important',
          },
          '& .MuiDataGrid-cell:not(:last-child)': {
            fontFamily: MobergFontFamily.REGULAR,
            fontSize: '1rem',
            backgroundColor: 'var(--color-table-cell-bg) !important',
            color: 'var(--color-table-cell-text) !important',
            borderColor: 'var(--color-table-border)',
          },
          '& .MuiDataGrid-cell:last-child': {
            fontFamily: MobergFontFamily.REGULAR,
            fontSize: '1rem',
            backgroundColor: 'var(--color-table-cell-bg) !important',
            color: 'var(--color-table-cell-text) !important',
          },
          '& .MuiDataGrid-cellContent': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          },
          // Specific styling for cells containing buttons (View column)
          '& .MuiDataGrid-cell:has(.MuiButton-root)': {
            display: 'grid',
            placeItems: 'center',
          },
          '& .MuiDataGrid-row': {
            fontFamily: MobergFontFamily.REGULAR,
          },
          // Apply font to pagination
          '& .MuiTablePagination-root': {
            fontFamily: MobergFontFamily.REGULAR,
            color: 'var(--color-table-cell-text) !important',
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: 'var(--color-table-header-bg) !important',
            color: 'var(--color-table-cell-text) !important',
            borderColor: 'var(--color-table-border)',
          },
          // Apply font to toolbar
          '& .MuiDataGrid-toolbarContainer': {
            fontFamily: MobergFontFamily.REGULAR,
          },
          // Disable row highlighting if requested
          ...(disableHighlightRows && {
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'var(--color-table-cell-bg)',
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
              backgroundColor: 'var(--color-table-cell-bg) !important',
              color: 'var(--color-table-cell-text) !important',
            },
          }),
        }}
        {...rest}
      />
    </div>
  );
}

ModernMUITable.propTypes = {
  // Data fetching props
  queryKey: PropTypes.string,
  endpoint: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  body: PropTypes.object,
  optionalParams: PropTypes.object,
  supportsPageSize: PropTypes.bool, // New prop to control whether to send page size to API
  
  columns: PropTypes.array.isRequired,
  tableData: PropTypes.array,
  rowCount: PropTypes.number,
  paginationModel: PropTypes.object,
  onPaginationModelChange: PropTypes.func,
  paginationMode: PropTypes.oneOf(['client', 'server']),
  loading: PropTypes.bool,
  isRowSelectable: PropTypes.func,
  checkboxSelection: PropTypes.bool,
  useDefaultMuiStyling: PropTypes.bool,
  disableSelectAll: PropTypes.bool,
  columnVisibilityModel: PropTypes.object,
  onColumnVisibilityModelChange: PropTypes.func,
  sortingMode: PropTypes.oneOf(['client', 'server']),
  onSortModelChange: PropTypes.func,
  toolbar: PropTypes.elementType,
  tableToolbarProps: PropTypes.object,
  showToolbar: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dense: PropTypes.bool,
  disableRowSelectionOnClick: PropTypes.bool,
  onRowSelectionModelChange: PropTypes.func,
  tableRef: PropTypes.object,
  overrideStyles: PropTypes.object,
  disableHighlightRows: PropTypes.bool,
  enableRowSelection: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  useAdvancedToolbar: PropTypes.bool,
  pageSizeOptions: PropTypes.array,
  initialState: PropTypes.object,
  
  // URL STATE PERSISTENCE PROPS
  persistStateInUrl: PropTypes.bool,
  tableId: PropTypes.string,
  urlSyncDebounceMs: PropTypes.number,
  
  // DEFAULT SORTING PROP
  defaultSortModel: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string.isRequired,
    sort: PropTypes.oneOf(['asc', 'desc']).isRequired,
  })),
  
  // BACKWARD COMPATIBILITY PROPS FOR OLD MUTABLE API
  handleOnRowClick: PropTypes.func, // Function to handle row clicks (old API)
  isCheckboxSelection: PropTypes.bool, // Boolean for checkbox selection (old API) - maps to checkboxSelection
  disableMultiSelect: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]), // Function or boolean for multi-select behavior (old API)
  preselectedProps: PropTypes.object, // Object with selectedRows and setSelectedRows (old API)
  isAutoselect: PropTypes.bool, // Boolean for auto-selecting single rows (old API)
  paginationType: PropTypes.string, // Pagination type (old API)
  onSearchValueChanged: PropTypes.func, // Search change callback (old API)
  autoPageSizeProps: PropTypes.object, // Auto page size configuration (old API)
}; 